import type { TFunction } from 'i18next';
import type { PasswordConfig, PasswordValidationResult } from '@/types/passwordConfig';

const getRequiredCount = (value: number | undefined, enabled: boolean | undefined) => {
  if (!enabled) {
    return 0;
  }
  return value ?? 1;
};

const getCharacterRequirementParts = (config: PasswordConfig, t: TFunction) => {
  const parts: string[] = [];
  const separator = t('common:password.list-separator');

  if (config.lowercaseCharacter) {
    parts.push(t('common:password.characters.lowercase'));
  }
  if (config.uppercaseCharacter) {
    parts.push(t('common:password.characters.uppercase'));
  }
  if (config.digitCharacter) {
    parts.push(t('common:password.characters.digit'));
  }
  if (config.nonAlphanumericCharacter) {
    parts.push(t('common:password.characters.special'));
  }

  return parts.join(separator);
};

export const getPasswordRequirements = (
  config: PasswordConfig | null | undefined,
  t: TFunction
): string[] => {
  if (!config) {
    return [];
  }

  const requirements: string[] = [];

  if (config.length && config.minLength) {
    if (config.maxLength) {
      requirements.push(
        t('common:password.requirements.length-range', {
          min: config.minLength,
          max: config.maxLength,
        })
      );
    }
    else {
      requirements.push(
        t('common:password.requirements.min-length', { count: config.minLength })
      );
    }
  }

  if (config.characterCharacteristics) {
    const parts = getCharacterRequirementParts(config, t);
    if (parts) {
      requirements.push(t('common:password.requirements.includes', { parts }));
    }
  }

  return requirements;
};

const checkSequence = (
  password: string,
  sequenceType: 'alphabetical' | 'numerical' | 'qwerty',
  sequenceLength: number,
  wrap?: boolean
) => {
  const sequences = {
    alphabetical: 'abcdefghijklmnopqrstuvwxyz',
    numerical: '0123456789',
    qwerty: 'qwertyuiopasdfghjklzxcvbnm',
  };

  const sequence = sequences[sequenceType];
  const normalizedPassword = password.toLowerCase();

  for (let i = 0; i <= sequence.length - sequenceLength; i++) {
    const seq = sequence.substr(i, sequenceLength);
    if (normalizedPassword.includes(seq)) {
      return true;
    }

    if (wrap && i === sequence.length - sequenceLength) {
      const wrappedSeq = sequence.substr(i) + sequence.substr(0, sequenceLength - (sequence.length - i));
      if (normalizedPassword.includes(wrappedSeq)) {
        return true;
      }
    }
  }

  return false;
};

export const createPasswordValidator = (
  config: PasswordConfig | null | undefined,
  t: TFunction
) => {
  if (!config) {
    return () => ({
      isValid: true,
      strength: 0,
      errors: [],
      warnings: [],
      requirements: [],
    } satisfies PasswordValidationResult);
  }

  return (password: string = ''): PasswordValidationResult => {
    const errors: string[] = [];
    const warnings: string[] = [];
    const requirements = getPasswordRequirements(config, t);

    if (config.length) {
      if (config.minLength && password.length < config.minLength) {
        errors.push(t('common:password.errors.min-length', { count: config.minLength }));
      }
      if (config.maxLength && password.length > config.maxLength) {
        errors.push(t('common:password.errors.max-length', { count: config.maxLength }));
      }
    }

    if (config.characterCharacteristics) {
      const requiredDigits = getRequiredCount(config.numberOfDigits, config.digitCharacter);
      if (requiredDigits > 0) {
        const digitCount = (password.match(/\d/g) || []).length;
        if (digitCount < requiredDigits) {
          errors.push(t('common:password.errors.min-digits', { count: requiredDigits }));
        }
      }

      const requiredNonAlphanumerics = getRequiredCount(config.numberOfNonAlphanumerics, config.nonAlphanumericCharacter);
      if (requiredNonAlphanumerics > 0) {
        const nonAlphaNumCount = (password.match(/[^a-zA-Z0-9]/g) || []).length;
        if (nonAlphaNumCount < requiredNonAlphanumerics) {
          errors.push(t('common:password.errors.min-non-alphanumeric', { count: requiredNonAlphanumerics }));
        }
      }

      const requiredUppercases = getRequiredCount(config.numberOfUppercases, config.uppercaseCharacter);
      if (requiredUppercases > 0) {
        const upperCount = (password.match(/[A-Z]/g) || []).length;
        if (upperCount < requiredUppercases) {
          errors.push(t('common:password.errors.min-uppercase', { count: requiredUppercases }));
        }
      }

      const requiredLowercases = getRequiredCount(config.numberOfLowercases, config.lowercaseCharacter);
      if (requiredLowercases > 0) {
        const lowerCount = (password.match(/[a-z]/g) || []).length;
        if (lowerCount < requiredLowercases) {
          errors.push(t('common:password.errors.min-lowercase', { count: requiredLowercases }));
        }
      }
    }

    if (config.whitespace && /\s/.test(password)) {
      errors.push(t('common:password.errors.whitespace'));
    }

    if (config.alphabeticalSequence) {
      const sequenceLength = config.alphabeticalSequenceLength ?? 2;
      if (checkSequence(password, 'alphabetical', sequenceLength, config.wrapAlphabeticalSequence)) {
        errors.push(t('common:password.errors.alphabetical-sequence', { count: sequenceLength }));
      }
    }

    if (config.numericalSequence) {
      const sequenceLength = config.numericalSequenceLength ?? 2;
      if (checkSequence(password, 'numerical', sequenceLength, config.wrapNumericalSequence)) {
        errors.push(t('common:password.errors.numerical-sequence', { count: sequenceLength }));
      }
    }

    if (config.qwertySequence) {
      const sequenceLength = config.qwertySequenceLength ?? 2;
      if (checkSequence(password, 'qwerty', sequenceLength, config.wrapQwertySequence)) {
        errors.push(t('common:password.errors.qwerty-sequence', { count: sequenceLength }));
      }
    }

    if (config.repeatCharacterRegex) {
      const repeatLength = config.repeatCharacterSequenceLength ?? 2;
      const regex = new RegExp(`(.)\\1{${repeatLength - 1},}`);
      if (regex.test(password)) {
        errors.push(t('common:password.errors.repeated-characters', { count: repeatLength }));
      }
    }

    let strength = 0;
    if (errors.length === 0 && password.length > 0) {
      strength = 50;

      if (config.length && config.minLength && config.maxLength) {
        const lengthPercent = Math.min(
          100,
          ((password.length - config.minLength) / (config.maxLength - config.minLength)) * 100
        );
        strength += lengthPercent * 0.3;
      }

      const varietyScore = (
        (config.digitCharacter && /\d/.test(password) ? 5 : 0) +
        (config.nonAlphanumericCharacter && /[^a-zA-Z0-9]/.test(password) ? 5 : 0) +
        (config.uppercaseCharacter && /[A-Z]/.test(password) ? 5 : 0) +
        (config.lowercaseCharacter && /[a-z]/.test(password) ? 5 : 0)
      );
      strength += varietyScore;
      strength -= warnings.length * 5;
      strength = Math.max(0, Math.min(100, strength));
    }

    return {
      isValid: errors.length === 0,
      strength,
      errors,
      warnings,
      requirements,
    };
  };
};
